import { createRoot } from "react-dom/client"
import "./style.css"
import {
  ServerProvider,
  setCurrentApp,
  useScopedQueryPersistence,
} from "@mimber/client"
import { ThemeProvider } from "@mimber/ui-web"
import {
  AuthProvider,
  FeatureToggleProvider,
  useAuth,
  getAccessToken,
} from "./core/contexts"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { routeTree } from "./routeTree.gen"
import { ConfigGate } from "./components"
import { getSelectedOrganizationId } from "@/utils/organization"
import { configureAmplify } from "@/config/amplify"
import { initializeSentry } from "@/config/sentry"
import * as Sentry from "@sentry/react"

const isMockedAuthTier = import.meta.env.VITE_E2E_MOCK_AUTH === "true"

// Initialize app type for client guards
setCurrentApp("mimber-organization")

// Initialize monitoring
initializeSentry()

// Playwright's mocked tier never talks to real Cognito, so Amplify never
// needs configuring there (and doing so would require real Cognito env vars).
if (!isMockedAuthTier) {
  configureAmplify()
}

const QUERY_CACHE_KEY_PREFIX = "mimber-organization-query-cache"
const cacheBusterVersion = process.env.VITE_APP_VERSION

// Create a router instance
const router = createRouter({
  routeTree,
  context: {
    // auth will initially be undefined - we'll pass it down from InnerApp
    auth: undefined!,
  },
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const App = () => {
  const { isLoading, user } = useAuth()
  const apiUrl =
    import.meta.env.VITE_MIMBER_ORG_API_URL || "http://localhost:3002"
  const persistedQueryCache = useScopedQueryPersistence({
    cacheBusterVersion,
    isLoading,
    keyPrefix: QUERY_CACHE_KEY_PREFIX,
    storage: window.localStorage,
    userSub: user?.sub,
  })

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
      <ThemeProvider storageKey="mimber-organization-theme">
        <ServerProvider
          appAgent="MimberOrganization"
          cacheBusterVersion={persistedQueryCache.cacheBusterVersion}
          customServerConfig={{ domain: apiUrl }}
          getAccessToken={async () => {
            const token = await getAccessToken()
            if (!token) {
              throw new Error("No access token available")
            }
            return token
          }}
          getOrganizationId={getSelectedOrganizationId}
          // persister disabled pending cache-bug investigation (docs/plans/add-member-improvements/plan.md, Phase F)
          // persister={persistedQueryCache.persister}
        >
          <ConfigGate>
            <FeatureToggleProvider>
              <RouterProvider router={router} />
            </FeatureToggleProvider>
          </ConfigGate>
        </ServerProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

const ErrorFallback = () => {
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h1>Something went wrong</h1>
      <p>We've been notified and are working on a fix.</p>
      <button onClick={() => window.location.reload()}>Reload page</button>
    </div>
  )
}

createRoot(document.getElementById("app")!).render(
  <AuthProvider>
    <App />
  </AuthProvider>
)
